import type { ILoan } from 'pages/LoanStructure/interfaces'

import { removeComma } from './convertor'

const MORTGAGE_LIABILITY_TYPES = ['Mortgage', 'HELOC']
const PRIMARY_OCCUPANCY_TYPES = ['Owner Occupied', 'Partially Owner Occupied']

function getBorrowerIncome(borrowerData: Record<string, any>): number {
  let total = 0

  const primaryGMI = borrowerData?.primaryEmployer?.grossMonthlyIncome
  if (Array.isArray(primaryGMI)) {
    primaryGMI.forEach((row: any) => {
      total += removeComma(row?.amount)
    })
  }

  const secondaryRows = borrowerData?.secondaryEmployment?.rows
  if (Array.isArray(secondaryRows)) {
    secondaryRows.forEach((employer: any) => {
      const gmi = employer?.grossMonthlyIncome
      if (Array.isArray(gmi)) {
        gmi.forEach((row: any) => {
          total += removeComma(row?.amount)
        })
      }
    })
  }

  const otherIncomeRows = borrowerData?.otherIncome?.rows
  if (Array.isArray(otherIncomeRows) && !borrowerData?.otherIncome?.doNotApply) {
    otherIncomeRows.forEach((row: any) => {
      total += removeComma(row?.amount)
    })
  }

  return total
}

function getREOData(borrowerState: Record<string, any>): {
  netPositiveREOIncome: number
  netREOLoss: number
} {
  let netPositiveREOIncome = 0
  let netREOLoss = 0

  Object.keys(borrowerState).forEach((sep) => {
    const b = borrowerState[sep]
    if (!b?.isIncluded) return

    const rentalEstates = b?.rentalEstates
    const liabilities = b?.liabilities
    if (!Array.isArray(rentalEstates)) return

    rentalEstates.forEach((estate: any) => {
      const grossIncome = removeComma(estate.grossRentalIncome)
      if (!grossIncome && !estate.mortgagePayments) return

      const vacancyFactor = estate.vacancyFactor ?? 0
      const incomeLessVacancy = (grossIncome * (100 - vacancyFactor)) / 100

      const liability = Array.isArray(liabilities)
        ? liabilities.find((item: any) => item.propertyId == estate.id)
        : undefined
      const mortgagePayments = removeComma(estate.mortgagePayments || liability?.monthlyPayment)
      const tiHod = estate.hodIncluded ? 0 : removeComma(estate.tiHod)

      const netRentalIncome = incomeLessVacancy - mortgagePayments - tiHod

      if (netRentalIncome > 0) {
        netPositiveREOIncome += netRentalIncome
      } else if (netRentalIncome < 0) {
        netREOLoss += Math.abs(netRentalIncome)
      }
    })
  })

  return { netPositiveREOIncome, netREOLoss }
}

function getIncludedMonthlyLiabilities(borrowerState: Record<string, any>): number {
  let total = 0

  Object.keys(borrowerState).forEach((sep) => {
    const b = borrowerState[sep]
    if (!b?.isIncluded) return

    if (Array.isArray(b?.liabilities) && !b?.liabilitiesNotApply) {
      b.liabilities.forEach((liability: any) => {
        if (liability.notCounted) return
        if (liability.toBePaid) return
        if (liability.omitted) return
        if (MORTGAGE_LIABILITY_TYPES.includes(liability.accountType)) return
        total += removeComma(liability.monthlyPayment)
      })
    }

    if (Array.isArray(b?.expenses?.rows) && !b?.expenses?.doNotApply) {
      b.expenses.rows.forEach((expense: any) => {
        total += removeComma(expense.moPayment)
      })
    }
  })

  return total
}

export interface DTIResult {
  firstDTI: number
  secondDTI: number
  totalQualifyingIncome: number
  presentPITIA: number
  proposedPITIA: number
  negativeSubjectCashFlow: number
  netREOLoss: number
  includedMonthlyLiabilities: number
  totalAdditionalDebt: number
  totalMonthlyDebt: number
  netPositiveREOIncome: number
}

export interface CalculateDTIParams {
  loan: ILoan
  borrowerState: Record<string, any>
  proposedPITIA: number
  presentPITIA: number
}

export const calculateDTI = ({ loan, borrowerState, presentPITIA, proposedPITIA }: CalculateDTIParams): DTIResult => {
  const isPrimaryResidence = PRIMARY_OCCUPANCY_TYPES.includes(loan.proposedOccupancy)

  let totalBorrowerIncome = 0
  Object.keys(borrowerState).forEach((sep) => {
    const b = borrowerState[sep]
    if (!b?.isIncluded) return
    totalBorrowerIncome += getBorrowerIncome(b)
  })

  const { netPositiveREOIncome, netREOLoss } = getREOData(borrowerState)
  const totalQualifyingIncome = totalBorrowerIncome + netPositiveREOIncome

  const grossSubjectRentalIncome = removeComma(loan.proposedMonthlyRent)
  const negativeSubjectCashFlow = Math.max(proposedPITIA - grossSubjectRentalIncome, 0)

  const includedMonthlyLiabilities = getIncludedMonthlyLiabilities(borrowerState)

  const emptyResult: DTIResult = {
    firstDTI: NaN,
    secondDTI: NaN,
    totalQualifyingIncome: 0,
    presentPITIA,
    proposedPITIA,
    negativeSubjectCashFlow,
    netREOLoss,
    includedMonthlyLiabilities,
    totalAdditionalDebt: 0,
    totalMonthlyDebt: 0,
    netPositiveREOIncome,
  }

  if (totalQualifyingIncome <= 0) return emptyResult

  let frontEndNumerator: number
  let backEndNumerator: number

  if (isPrimaryResidence) {
    frontEndNumerator = proposedPITIA
    const totalAdditionalDebt = netREOLoss + includedMonthlyLiabilities
    backEndNumerator = proposedPITIA + totalAdditionalDebt
  } else {
    frontEndNumerator = presentPITIA
    const totalAdditionalDebt = negativeSubjectCashFlow + netREOLoss + includedMonthlyLiabilities
    backEndNumerator = presentPITIA + totalAdditionalDebt
  }

  const frontEndDTI = (frontEndNumerator / totalQualifyingIncome) * 100
  const backEndDTI = (backEndNumerator / totalQualifyingIncome) * 100

  const totalAdditionalDebt = isPrimaryResidence
    ? netREOLoss + includedMonthlyLiabilities
    : negativeSubjectCashFlow + netREOLoss + includedMonthlyLiabilities

  return {
    firstDTI: Number(frontEndDTI.toFixed(2)),
    secondDTI: Number(backEndDTI.toFixed(2)),
    totalQualifyingIncome,
    presentPITIA,
    proposedPITIA,
    negativeSubjectCashFlow,
    netREOLoss,
    includedMonthlyLiabilities,
    totalAdditionalDebt,
    totalMonthlyDebt: backEndNumerator,
    netPositiveREOIncome,
  }
}
